import { FC } from 'react';
import {
  Controller,
  FieldError,
  RegisterOptions,
  get,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { Box, InputLabel } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

import { Input, InputProps } from '@/components/FormElements/Input';
import { UnitAdornment } from '@/components/FormElements/Input/UnitAdornment';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import { useVerifiedFields } from '@/contexts';

import type { MasterNameFormData } from './MasterNameForm';

type HeightFieldName =
  | 'physicalCharacteristics.heightExact.feet'
  | 'physicalCharacteristics.heightExact.inches';

type HeightFieldConfig = {
  name: HeightFieldName;
  unit: 'ft' | 'in';
  roundedRight: boolean;
  sx: SxProps<Theme>;
  inputProps: NonNullable<InputProps['inputProps']> & {
    min: number;
    step: number;
    max?: number;
    'aria-label': string;
  };
  error?: FieldError;
  rules: RegisterOptions<MasterNameFormData, HeightFieldName>;
};

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== '';

const toNumberOrEmptyString = (value: string): number | '' =>
  value === '' ? '' : Number(value);

const validateHeightFeet = (value: unknown, inches: unknown) => {
  if (!hasValue(value) && !hasValue(inches)) {
    return true;
  }

  if (!hasValue(value)) {
    return 'Feet is required when inches is provided.';
  }

  const parsedValue = Number(value);
  if (!Number.isInteger(parsedValue) || parsedValue < 0) {
    return 'Feet must be a non-negative whole number.';
  }

  return true;
};

const validateHeightInches = (value: unknown, feet: unknown) => {
  if (!hasValue(value)) {
    return true;
  }

  if (!hasValue(feet)) {
    return 'Feet is required when inches is provided.';
  }

  const parsedValue = Number(value);
  if (Number.isNaN(parsedValue) || parsedValue < 0) {
    return 'Inches must be a non-negative number.';
  }

  if (parsedValue >= 12) {
    return 'Inches must be less than 12.';
  }

  return true;
};

export const HeightExactField: FC<{ disabled?: boolean }> = ({
  disabled = false,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<MasterNameFormData>();
  const { isFieldVerified } = useVerifiedFields();
  const heightExact = useWatch({
    control,
    name: 'physicalCharacteristics.heightExact',
  });

  const feetError = get(errors, 'physicalCharacteristics.heightExact.feet') as
    | FieldError
    | undefined;
  const inchesError = get(
    errors,
    'physicalCharacteristics.heightExact.inches',
  ) as FieldError | undefined;
  const heightVerified =
    isFieldVerified('physicalCharacteristics.heightExact.feet') &&
    isFieldVerified('physicalCharacteristics.heightExact.inches');

  const fields: HeightFieldConfig[] = [
    {
      name: 'physicalCharacteristics.heightExact.feet' as const,
      unit: 'ft',
      roundedRight: false,
      sx: {
        '& .MuiInputBase-root': {
          borderRadius: '6px 0 0 6px',
        },
      },
      inputProps: {
        min: 0,
        step: 1,
        'aria-label': 'Height feet',
      },
      error: feetError,
      rules: {
        deps: ['physicalCharacteristics.heightExact.inches'],
        validate: (value: unknown) =>
          validateHeightFeet(value, heightExact?.inches),
      },
    },
    {
      name: 'physicalCharacteristics.heightExact.inches' as const,
      unit: 'in',
      roundedRight: true,
      sx: {
        marginLeft: '-1px',
        '& .MuiInputBase-root': {
          borderRadius: '0 6px 6px 0',
        },
      },
      inputProps: {
        min: 0,
        max: 11.99,
        step: 0.01,
        'aria-label': 'Height inches',
      },
      error: inchesError,
      rules: {
        deps: ['physicalCharacteristics.heightExact.feet'],
        validate: (value: unknown) =>
          validateHeightInches(value, heightExact?.feet),
      },
    },
  ];

  return (
    <Box display="flex" alignItems="flex-end" width="100%">
      <Box flex={1}>
        <InputLabel>Height (ft/in)</InputLabel>
        <Box display="flex" width="100%">
          {fields.map((item) => (
            <Controller
              key={item.name}
              name={item.name}
              control={control}
              rules={item.rules}
              defaultValue={undefined}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  disabled={disabled}
                  value={field.value ?? ''}
                  hideSpinner
                  placeholder="/"
                  showLabel={false}
                  error={
                    item.error?.message ? String(item.error.message) : undefined
                  }
                  sx={item.sx}
                  inputProps={item.inputProps}
                  endAdornment={
                    <UnitAdornment
                      label={item.unit}
                      roundedRight={item.roundedRight}
                    />
                  }
                  onChange={(event) => {
                    field.onChange(toNumberOrEmptyString(event.target.value));
                  }}
                />
              )}
            />
          ))}
        </Box>
      </Box>
      {heightVerified ? (
        <VerifiedBadge tooltip="Verified by state ID" className="mb-4 ml-2" />
      ) : null}
    </Box>
  );
};
